import { useRef } from 'react'
import { VisualCrop, VisualZoom } from 'app/frontend/types/custom/VisualPlan'
import { ZOOM_MAX, ZOOM_MIN, ZOOM_WHEEL_STEP } from '../constants'

type Params = {
  width: number
  height: number
  zoom: VisualZoom | undefined
  crop?: VisualCrop
  onZoomChange: (zoom: VisualZoom | undefined) => void
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const cropOffsetY = (crop: VisualCrop | undefined) =>
  crop && crop.height < 1 ? crop.y * (1 - crop.height) : 0

export const contentPointUnderCursor = (
  zoom: VisualZoom | undefined,
  pointer: { x: number; y: number },
  crop?: VisualCrop,
) => {
  const scale = zoom && zoom.scale > 1 ? zoom.scale : 1
  const panX = zoom && zoom.scale > 1 ? zoom.x : 0
  const panY = zoom && zoom.scale > 1 ? zoom.y : 0
  return {
    x: (pointer.x + panX * (scale - 1)) / scale,
    y: (pointer.y + cropOffsetY(crop) + panY * (scale - 1)) / scale,
  }
}

export const zoomAtPointer = (
  zoom: VisualZoom | undefined,
  nextScale: number,
  pointer: { x: number; y: number },
  crop?: VisualCrop,
): VisualZoom | undefined => {
  const scale = clamp(nextScale, ZOOM_MIN, ZOOM_MAX)
  if (scale <= 1) return undefined

  const content = contentPointUnderCursor(zoom, pointer, crop)
  return {
    scale,
    x: clamp((content.x * scale - pointer.x) / (scale - 1), 0, 1),
    y: clamp(
      (content.y * scale - pointer.y - cropOffsetY(crop)) / (scale - 1),
      0,
      1,
    ),
  }
}

export const useCanvasZoomPan = ({
  width,
  height,
  zoom,
  crop,
  onZoomChange,
}: Params) => {
  const panRef = useRef<{
    clientX: number
    clientY: number
    originX: number
    originY: number
  } | null>(null)

  const isPanning = () => panRef.current !== null

  const onPanStart = (clientX: number, clientY: number) => {
    if (!zoom || zoom.scale <= 1) return false
    panRef.current = {
      clientX,
      clientY,
      originX: zoom.x,
      originY: zoom.y,
    }
    return true
  }

  const onPanMove = (clientX: number, clientY: number) => {
    if (!panRef.current || !zoom || zoom.scale <= 1) return
    const scaleFactor = zoom.scale - 1
    if (scaleFactor <= 0 || width === 0 || height === 0) return
    const dx = -(clientX - panRef.current.clientX) / (width * scaleFactor)
    const dy = -(clientY - panRef.current.clientY) / (height * scaleFactor)
    onZoomChange({
      ...zoom,
      x: clamp(panRef.current.originX + dx, 0, 1),
      y: clamp(panRef.current.originY + dy, 0, 1),
    })
  }

  const onPanEnd = () => {
    panRef.current = null
  }

  const onWheelZoom = (
    pointerX: number,
    pointerY: number,
    deltaY: number,
  ): boolean => {
    if (width === 0 || height === 0 || deltaY === 0) return false
    const currentScale = zoom && zoom.scale > 1 ? zoom.scale : 1
    const direction = deltaY > 0 ? -1 : 1
    const nextScale = clamp(
      Math.round((currentScale + direction * ZOOM_WHEEL_STEP) * 100) / 100,
      ZOOM_MIN,
      ZOOM_MAX,
    )
    if (nextScale === currentScale) return false
    onZoomChange(
      zoomAtPointer(
        zoom,
        nextScale,
        {
          x: pointerX / width,
          y: pointerY / height,
        },
        crop,
      ),
    )
    return true
  }

  return { onPanStart, onPanMove, onPanEnd, isPanning, onWheelZoom }
}
